import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider
} from '@mui/material';
import { collection, getDocs, setDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CompressIcon from '@mui/icons-material/Compress';

const GOLD = '#d4af37';
const COLLECTION = 'gallery_photos';

interface PhotoItem {
  id: string;
  title: string;
  imageUrl: string;
  category: string;
  order: number;
}

const PhotosGalleryManager = () => {
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Resize/compress state
  const [resizeDialogOpen, setResizeDialogOpen] = useState(false);
  const [resizePhotoId, setResizePhotoId] = useState<string | null>(null);
  const [resizeSettings, setResizeSettings] = useState({ maxWidth: 1920, maxHeight: 1080, quality: 85 });
  const [resizing, setResizing] = useState(false);
  const [originalSize, setOriginalSize] = useState(0);
  const [compressedSize, setCompressedSize] = useState(0);

  // Resize/compress utility
  const resizeImage = (
    source: File | string,
    maxWidth: number,
    maxHeight: number,
    quality: number
  ): Promise<{ base64: string; size: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas context failed')); return; }
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        const base64 = canvas.toDataURL('image/jpeg', quality / 100);
        const size = Math.round((base64.length * 3) / 4);
        resolve({ base64, size });
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      if (typeof source === 'string') {
        img.src = source;
      } else {
        const reader = new FileReader();
        reader.onload = (e) => { img.src = e.target?.result as string; };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(source);
      }
    });
  };

  // File upload handler — stores base64 locally, saved on "Save" click
  const handleFileUpload = async (photoId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showSnackbar('Please select an image file', 'error'); return; }

    setUploading(photoId);
    setUploadProgress(0);
    try {
      setUploadProgress(20);
      const fileSizeKB = file.size / 1024;
      let base64Result: string;

      if (file.size > 500 * 1024) {
        showSnackbar(`Image is ${(fileSizeKB / 1024).toFixed(2)}MB. Auto-compressing...`, 'success');
        setUploadProgress(40);
        const { base64 } = await resizeImage(file, 1920, 1080, 80);
        base64Result = base64;
        setUploadProgress(80);
        const newSizeKB = Math.round((base64.length * 3) / 4) / 1024;
        showSnackbar(`Compressed to ${(newSizeKB / 1024).toFixed(2)}MB`, 'success');
      } else {
        base64Result = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsDataURL(file);
        });
        setUploadProgress(80);
      }

      handlePhotoChange(photoId, 'imageUrl', base64Result);
      setUploadProgress(100);
      setTimeout(() => { setUploading(null); setUploadProgress(0); showSnackbar('Image loaded. Click "Save" to store.', 'success'); }, 500);
    } catch (error: any) {
      showSnackbar('Failed to process image: ' + (error.message || 'Unknown error'), 'error');
      setUploading(null);
      setUploadProgress(0);
    }
  };

  // Open resize dialog
  const openResizeDialog = (photoId: string) => {
    const photo = photos.find(p => p.id === photoId);
    if (photo?.imageUrl) {
      setResizePhotoId(photoId);
      const sizeBytes = Math.round((photo.imageUrl.length * 3) / 4);
      setOriginalSize(sizeBytes);
      setCompressedSize(0);
      setResizeDialogOpen(true);
    }
  };

  // Preview compression size
  const previewCompression = async () => {
    if (!resizePhotoId) return;
    const photo = photos.find(p => p.id === resizePhotoId);
    if (!photo?.imageUrl) return;
    try {
      const { size } = await resizeImage(photo.imageUrl, resizeSettings.maxWidth, resizeSettings.maxHeight, resizeSettings.quality);
      setCompressedSize(size);
    } catch (error) { console.error('Preview error:', error); }
  };

  // Apply resize
  const applyResize = async () => {
    if (!resizePhotoId) return;
    const photo = photos.find(p => p.id === resizePhotoId);
    if (!photo?.imageUrl) return;
    setResizing(true);
    try {
      const { base64 } = await resizeImage(photo.imageUrl, resizeSettings.maxWidth, resizeSettings.maxHeight, resizeSettings.quality);
      handlePhotoChange(resizePhotoId, 'imageUrl', base64);
      showSnackbar('Image compressed. Click "Save" to store.', 'success');
      setResizeDialogOpen(false);
    } catch (error: any) {
      showSnackbar('Failed to resize: ' + (error.message || 'Unknown error'), 'error');
    } finally { setResizing(false); }
  };

  // Load all photos — each photo is a separate document
  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTION));
      const fetched: PhotoItem[] = [];
      querySnapshot.forEach((docSnap) => {
        fetched.push({ id: docSnap.id, ...docSnap.data() } as PhotoItem);
      });
      setPhotos(fetched.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    } catch (error) {
      console.error('Error loading photos:', error);
      showSnackbar('Failed to load photos', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, []);

  // Save a single photo as its own Firestore document
  const handleSavePhoto = async (photo: PhotoItem) => {
    if (!photo.imageUrl) {
      showSnackbar('Please add an image first', 'error');
      return;
    }
    setSavingId(photo.id);
    try {
      const docId = photo.id.startsWith('new_') ? `photo_${photo.order}_${Date.now()}` : photo.id;
      await setDoc(doc(db, COLLECTION, docId), {
        imageUrl: photo.imageUrl,
        title: photo.title || '',
        category: photo.category || 'general',
        order: photo.order
      });
      showSnackbar(`Photo "${photo.title}" saved successfully`, 'success');
      await fetchPhotos();
    } catch (error: any) {
      console.error('Error saving photo:', error);
      showSnackbar('Failed to save photo: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setSavingId(null);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddPhoto = () => {
    const newPhoto: PhotoItem = {
      id: `new_${Date.now()}`,
      title: 'New Photo',
      imageUrl: '',
      category: 'general',
      order: photos.length
    };
    setPhotos([...photos, newPhoto]);
  };

  const handlePhotoChange = (id: string, field: keyof PhotoItem, value: string) => {
    setPhotos(photos.map(photo =>
      photo.id === id ? { ...photo, [field]: value } : photo
    ));
  };

  const handleDeletePhoto = async (id: string) => {
    if (id.startsWith('new_')) {
      setPhotos(photos.filter(p => p.id !== id));
      return;
    }
    try {
      await deleteDoc(doc(db, COLLECTION, id));
      showSnackbar('Photo deleted', 'success');
      setPhotos(photos.filter(p => p.id !== id));
    } catch (error: any) {
      showSnackbar('Failed to delete: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Photos Gallery Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Each photo is saved individually — no single-document size limit
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchPhotos}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD }}>
            📷 Photos ({photos.length})
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddPhoto}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            Add Photo
          </Button>
        </Box>

        <Grid container spacing={2}>
          {photos.map((photo, index) => (
            <Grid item xs={12} key={photo.id}>
              <Paper sx={{ p: 2, bgcolor: '#fff', border: `1px solid ${GOLD}` }}>
                <Grid container spacing={2} alignItems="flex-start">
                  {/* Image preview */}
                  <Grid item xs={12} sm={3}>
                    <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 1, bgcolor: '#f9f9f9', minHeight: 120 }}>
                      {photo.imageUrl ? (
                        <Box
                          component="img"
                          src={
                            photo.imageUrl.startsWith('data:')
                              ? photo.imageUrl
                              : photo.imageUrl.startsWith('http')
                                ? photo.imageUrl
                                : `${process.env.PUBLIC_URL}${photo.imageUrl}`
                          }
                          alt={photo.title}
                          sx={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 1 }}
                          onError={(e: any) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <Box sx={{ width: '100%', height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#e0e0e0', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#999' }}>No image</Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Info fields */}
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      label="Photo Title"
                      value={photo.title}
                      onChange={(e) => handlePhotoChange(photo.id, 'title', e.target.value)}
                      size="small"
                      sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                    <TextField
                      fullWidth
                      label="Image URL (or upload below)"
                      value={photo.imageUrl.startsWith('data:') ? '(Uploaded image)' : photo.imageUrl}
                      onChange={(e) => handlePhotoChange(photo.id, 'imageUrl', e.target.value)}
                      size="small"
                      disabled={photo.imageUrl.startsWith('data:')}
                      sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                    <FormControl fullWidth size="small">
                      <InputLabel>Category</InputLabel>
                      <Select
                        label="Category"
                        value={photo.category || 'general'}
                        onChange={(e) => handlePhotoChange(photo.id, 'category', e.target.value)}
                      >
                        <MenuItem value="general">General</MenuItem>
                        <MenuItem value="festival">Festival</MenuItem>
                        <MenuItem value="ritual">Ritual</MenuItem>
                        <MenuItem value="deity">Deity</MenuItem>
                        <MenuItem value="architecture">Architecture</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Upload + compress + save + delete */}
                  <Grid item xs={12} sm={4}>
                    <Button
                      variant="contained"
                      component="label"
                      startIcon={uploading === photo.id ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                      disabled={uploading === photo.id}
                      fullWidth
                      sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700, mb: 1, '&:hover': { bgcolor: '#c9a227' }, '&:disabled': { bgcolor: '#ccc' } }}
                    >
                      {uploading === photo.id ? `Processing ${Math.round(uploadProgress)}%` : 'Upload Image'}
                      <input type="file" hidden accept="image/*" onChange={(e) => handleFileUpload(photo.id, e)} disabled={uploading === photo.id} />
                    </Button>

                    {uploading === photo.id && (
                      <Box sx={{ mb: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={uploadProgress}
                          sx={{ height: 5, borderRadius: 3, bgcolor: `${GOLD}30`, '& .MuiLinearProgress-bar': { bgcolor: GOLD, borderRadius: 3 } }}
                        />
                      </Box>
                    )}

                    {photo.imageUrl && (
                      <Button
                        variant="outlined"
                        startIcon={<CompressIcon />}
                        onClick={() => openResizeDialog(photo.id)}
                        fullWidth
                        sx={{ mb: 1, borderColor: '#2196f3', color: '#2196f3', '&:hover': { bgcolor: 'rgba(33,150,243,0.08)', borderColor: '#1976d2' } }}
                      >
                        Resize / Compress
                      </Button>
                    )}

                    <Button
                      variant="contained"
                      startIcon={savingId === photo.id ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                      onClick={() => handleSavePhoto(photo)}
                      disabled={savingId === photo.id || !photo.imageUrl}
                      fullWidth
                      sx={{ mb: 1, bgcolor: '#4caf50', color: '#fff', fontWeight: 700, '&:hover': { bgcolor: '#388e3c' }, '&:disabled': { bgcolor: '#ccc' } }}
                    >
                      {savingId === photo.id ? 'Saving...' : 'Save Photo'}
                    </Button>

                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeletePhoto(photo.id)}
                      fullWidth
                      size="small"
                    >
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Resize / Compress Dialog */}
      <Dialog open={resizeDialogOpen} onClose={() => setResizeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CompressIcon />
            Resize / Compress Image
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Adjust settings to resize and compress. Lower quality = smaller file size.
          </Typography>

          {originalSize > 0 && (
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, mb: 3 }}>
              <Typography variant="body2">
                <strong>Current Size:</strong> {(originalSize / 1024).toFixed(2)} KB
              </Typography>
              {compressedSize > 0 && (
                <Typography variant="body2" sx={{ color: 'green', mt: 0.5 }}>
                  <strong>Estimated New Size:</strong> {(compressedSize / 1024).toFixed(2)} KB
                  ({Math.round((1 - compressedSize / originalSize) * 100)}% smaller)
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Max Width: {resizeSettings.maxWidth}px
            </Typography>
            <Slider
              value={resizeSettings.maxWidth}
              onChange={(_, v) => setResizeSettings(s => ({ ...s, maxWidth: v as number }))}
              onChangeCommitted={previewCompression}
              min={640} max={2560} step={80}
              marks={[{ value: 640, label: '640' }, { value: 1280, label: '1280' }, { value: 1920, label: '1920' }, { value: 2560, label: '2560' }]}
              sx={{ color: GOLD }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Max Height: {resizeSettings.maxHeight}px
            </Typography>
            <Slider
              value={resizeSettings.maxHeight}
              onChange={(_, v) => setResizeSettings(s => ({ ...s, maxHeight: v as number }))}
              onChangeCommitted={previewCompression}
              min={360} max={1440} step={60}
              marks={[{ value: 360, label: '360' }, { value: 720, label: '720' }, { value: 1080, label: '1080' }, { value: 1440, label: '1440' }]}
              sx={{ color: GOLD }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Quality: {resizeSettings.quality}%
            </Typography>
            <Slider
              value={resizeSettings.quality}
              onChange={(_, v) => setResizeSettings(s => ({ ...s, quality: v as number }))}
              onChangeCommitted={previewCompression}
              min={30} max={100} step={5}
              marks={[{ value: 30, label: '30%' }, { value: 50, label: '50%' }, { value: 75, label: '75%' }, { value: 100, label: '100%' }]}
              sx={{ color: GOLD }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              70-80% is recommended for web images.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setResizeDialogOpen(false)} variant="outlined">Cancel</Button>
          <Button onClick={previewCompression} variant="outlined" sx={{ borderColor: GOLD, color: GOLD }}>Preview Size</Button>
          <Button
            onClick={applyResize}
            variant="contained"
            disabled={resizing}
            startIcon={resizing ? <CircularProgress size={16} color="inherit" /> : <CompressIcon />}
            sx={{ bgcolor: GOLD, color: '#000', '&:hover': { bgcolor: '#c9a227' } }}
          >
            {resizing ? 'Resizing...' : 'Apply Resize'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PhotosGalleryManager;
